import React, { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import Select from "react-select";
import {
  addQuarters,
  startOfQuarter,
  endOfQuarter,
  addYears,
  endOfYear,
} from "date-fns";

import Label from "@components/form/Label";
import Card from "@components/layout/Card";
import Layout from "@components/layout/Layout";
import Loading from "@components/shared/Loading";
import { getBillingReports } from "@services/reports";
import { useAllAccountingPeriods } from "@hooks/query/useAllAccountingPeriods";
import { formatDate } from "@utils/formatDate";
import SelectLevel1Resource from "@components/form/SelectLevel1Resource";

const BillingReports: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [renderers, setRenderers] = useState<any>({});
  const [SelectedReport, setSelectedReport] = useState<any>();
  const [reportProps, setReportProps] = useState<{
    report?: any;
    level1ResourceId?: string;
    fixedFromDate?: Date;
    fixedToDate?: Date;
    varFromDate?: Date;
    varToDate?: Date;
    runAt?: Date;
  }>({});

  const [filter, setFilter] = useState<{
    report?: any;
    level1ResourceId?: string;
    fixedFromDate?: Date;
    fixedToDate?: Date;
    varFromDate?: Date;
    varToDate?: Date;
  }>({});

  const { data: accountingPeriods = [] } = useAllAccountingPeriods({
    params: {
      level1ResourceId: filter.level1ResourceId,
    },
    options: {
      enabled: !!filter.level1ResourceId,
      refetchOnWindowFocus: false,
    },
  });

  useQuery(["reports", "billing"], getBillingReports, {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    onSuccess: data => {
      const updatedRenderers = { ...renderers };
      for (const report of data) {
        updatedRenderers[report.id] = lazy(
          () => import(`@components/reports/${report.type}`),
        );
      }

      setRenderers(updatedRenderers);
      setSelectedReport(undefined);

      const selectedReport = data.find((r: any) => r.id === id) || data[0];
      if (selectedReport) {
        setFilter({ ...filter, report: selectedReport });
      }
    },
  });

  const getDateRangeOptions = () => {
    const options = [];

    const uniquePeriods: any = {};
    accountingPeriods?.forEach((period: any) => {
      const uniqueKey = `${period.periodStart.toString()}-${period.periodEnd.toString()}`;
      if (!uniquePeriods[uniqueKey]) {
        uniquePeriods[uniqueKey] = period;
      }
    });

    const uniqueAccountingPeriods: any = Object.values(uniquePeriods);

    if (uniqueAccountingPeriods)
      for (const accountingPeriod of uniqueAccountingPeriods) {
        for (let i = 0; i < 4; i++) {
          const fixedQStart = startOfQuarter(
            addQuarters(new Date(accountingPeriod.periodStart), i + 1),
          );
          const fixedQEnd = endOfQuarter(fixedQStart);
          const varQStart = startOfQuarter(
            addQuarters(new Date(accountingPeriod.periodStart), i),
          );
          const varQEnd = endOfQuarter(varQStart);

          options.push({
            label: `Q${i + 1} ${new Date(
              accountingPeriod.periodStart,
            ).getFullYear()} (${formatDate(varQStart)} - ${formatDate(
              varQEnd,
            )})`,
            value: {
              fixedFromDate: new Date(fixedQStart),
              fixedToDate: new Date(fixedQEnd),
              varFromDate: new Date(varQStart),
              varToDate: new Date(varQEnd),
            },
          });
        }
        const fixedYStart = startOfQuarter(
          addYears(new Date(accountingPeriod.periodStart), 1),
        );
        const fixedYEnd = endOfQuarter(fixedYStart);
        const varYStart = new Date(accountingPeriod.periodStart);
        const varYEnd = endOfYear(varYStart);

        options.push({
          label: `Water Year ${new Date(
            accountingPeriod.periodStart,
          ).getFullYear()} (${formatDate(varYStart)} - ${formatDate(varYEnd)})`,
          value: {
            fixedFromDate: new Date(fixedYStart),
            fixedToDate: new Date(fixedYEnd),
            varFromDate: new Date(varYStart),
            varToDate: new Date(varYEnd),
          },
        });
      }

    return options;
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilter({
      ...filter,
      [field]: value,
    });
  };

  const handleDateRangeChange = (
    fixedFromDate: Date,
    fixedToDate: Date,
    varFromDate: Date,
    varToDate: Date,
  ) => {
    setFilter({
      ...filter,
      fixedFromDate,
      fixedToDate,
      varFromDate,
      varToDate,
    });
  };

  const handleRenderReport = (e: any) => {
    e.preventDefault();

    if (renderers[filter.report?.id]) {
      setSelectedReport(renderers[filter.report?.id]);
      setReportProps({
        report: filter.report,
        level1ResourceId: filter.level1ResourceId,
        fixedFromDate: filter.fixedFromDate,
        fixedToDate: filter.fixedToDate,
        varFromDate: filter.varFromDate,
        varToDate: filter.varToDate,
        runAt: new Date(),
      });
    }
  };

  return (
    <Layout
      permissions={["ViewReports"]}
      breadcrumb={[
        {
          label: t("dashboard.dashboard"),
          href: "/polestar",
        },
        {
          label: t("reporting.title"),
        },
      ]}
      title={t("reporting.new_billing_report")}
    >
      <Card className="flex flex-col rounded-none p-0 grow">
        <div className="flex flex-col grow gap-4 divide-y">
          <form
            className="flex items-end gap-2 p-4"
            onSubmit={handleRenderReport}
          >
            <div className="w-1/3">
              <Label htmlFor="report">{t("reporting.filter_level1wrs")}</Label>
              <SelectLevel1Resource
                className="w-full"
                value={filter.level1ResourceId}
                onChange={(e: any) => {
                  handleFilterChange("level1ResourceId", e.value);
                }}
              />
            </div>
            <div className="w-1/3">
              <Label htmlFor="report">{t("reporting.filter_dates")}</Label>
              <Select
                options={getDateRangeOptions()}
                className="w-full"
                value={getDateRangeOptions()?.find(
                  (e: any) =>
                    e.value.fixedFromDate.valueOf() ===
                      filter.fixedFromDate?.valueOf() &&
                    e.value.fixedToDate.valueOf() ===
                      filter.fixedToDate?.valueOf() &&
                    e.value.varFromDate.valueOf() ===
                      filter.varFromDate?.valueOf() &&
                    e.value.varToDate.valueOf() === filter.varToDate?.valueOf(),
                )}
                onChange={(e: any) => {
                  handleDateRangeChange(
                    e?.value.fixedFromDate,
                    e?.value.fixedToDate,
                    e?.value.varFromDate,
                    e?.value.varToDate,
                  );
                }}
              />
            </div>
            <div className="w-1/3 sm:w-1/5">
              <button
                type="submit"
                className="btn-secondary"
                disabled={
                  !filter.report || !filter.fixedFromDate || !filter.fixedToDate
                }
              >
                {t("reporting.get_data")}
              </button>
            </div>
          </form>
          {SelectedReport && (
            <div className="p-4 flex flex-col grow">
              <Suspense fallback={<Loading />}>
                <SelectedReport {...reportProps} />
              </Suspense>
            </div>
          )}
        </div>
      </Card>
    </Layout>
  );
};

export default BillingReports;
